import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

export interface Toast {
  id: string;
  type: "success" | "error" | "info" | "warning";
  title: string;
  message: string;
  duration: number;
}

@Injectable({ providedIn: "root" })
export class ToastService {
  private _toasts$ = new BehaviorSubject<Toast[]>([]);
  readonly toasts$ = this._toasts$.asObservable();

  success(message: string, title = "Success", duration = 3000) {
    this.add({ type: "success", title, message, duration });
  }

  /**
   * Ordinary errors auto-dismiss. Duration `0` stays until the user dismisses
   * it — used for job-status failures only.
   */
  error(message: string, title = "Error", duration = 5000) {
    this.add({ type: "error", title, message, duration });
  }

  info(message: string, title = "Info", duration = 3000) {
    this.add({ type: "info", title, message, duration });
  }

  warning(message: string, title = "Warning", duration = 3500) {
    this.add({ type: "warning", title, message, duration });
  }

  private add(toast: Omit<Toast, "id">) {
    // The HTTP interceptor and the screen both toast the same API error.
    // Keep one copy while it is still on screen.
    if (
      toast.type === "error" &&
      this._toasts$.value.some(
        (existing) =>
          existing.type === "error" && existing.message === toast.message,
      )
    ) {
      return;
    }
    const id = crypto.randomUUID();
    this._toasts$.next([...this._toasts$.value, { id, ...toast }]);
    if (toast.duration > 0) {
      setTimeout(() => this.dismiss(id), toast.duration);
    }
  }

  dismiss(id: string) {
    this._toasts$.next(this._toasts$.value.filter((t) => t.id !== id));
  }
}
